import React, {useState, useEffect} from 'react';
import {Copyright, Header} from "../components";
import Typography from "@mui/material/Typography";
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Button,
    Box,
    TextField,
    Grid,
    FormControlLabel,
    RadioGroup,
    FormLabel,
    Radio,
    Divider,
} from "@material-ui/core";
import useStyles from "../assets/styles/useStyles";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router";
import useGET from "../hooks/useGET";
import usePUT from "../hooks/usePUT";

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

function EditUser() {
    const [formValues, setFormValues] = useState({
        id: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        company: '',
        roles: ['ROLE_USER'],
    })
    const [utilValues, setUtilValues] = useState({
        selectedRole: "ROLE_USER",
        shownPhone: "",
    })
    const ROLE_HIERARCHY = {
        ROLE_MASTER: ["ROLE_MASTER", "ROLE_MANAGER", "ROLE_USER"],
        ROLE_MANAGER: ["ROLE_MANAGER", "ROLE_USER"],
        ROLE_USER: ["ROLE_USER"],
    };

    const [responseGetCompanies, sendGetRequestCompanies] = useGET();
    const [responseGetUsers, sendGetRequestUsers] = useGET();
    const [responsePut, sendPutRequest] = usePUT();
    const [companies, setCompanies] = useState([])
    const [selectedCompany, setSelectedCompany] = useState()
    const [users, setUsers] = useState([])
    const [selectedUser, setSelectedUser] = useState();
    const [userEdited, setUserEdited] = useState(false);
    const [error, setError] = useState({password: false, email: false, messages: []});

    const navigate = useNavigate();
    const reduxUser = useSelector(state => state.userReducer.user);

    const classes = useStyles();

    // check perm
    useEffect(() => {
        if (!reduxUser.roles?.includes('ROLE_MASTER')) {
            navigate("/")
        }
    }, [reduxUser.roles, navigate])

    // récupérer toutes les entreprises
    useEffect(() => {
        sendGetRequestCompanies({
            id: 'getCompanies',
            url: '/api/companies/getAllCompanies',
            headers: {
                Authorization: `Bearer ${reduxUser.token}`
            }
        })
    }, [sendGetRequestCompanies, reduxUser]);

    useEffect(() => {
        const response = responseGetCompanies

        if (!response) return;

        try {
            if (response.status < 200 || response.status >= 300) {
                console.log("error while retrieving companies")
            } else {
                setCompanies(response.data);
            }
        } catch (err) {
            console.log("error while retrieving companies: " + err)
        }

    }, [responseGetCompanies]);

    // récupérer tous les utilisateurs de l'entreprise sélectionnée
    useEffect(() => {
        if(!selectedCompany) {
            return;
        }
        sendGetRequestUsers({
            id: 'getCompanies',
            url: `/api/users/getUsersByCompanyId?companyId=${selectedCompany.id}`,
            headers: {
                Authorization: `Bearer ${reduxUser.token}`
            }
        })
    }, [sendGetRequestUsers, reduxUser, selectedCompany]);

    useEffect(() => {
        const response = responseGetUsers

        if (!response) return;

        try {
            if (response.status < 200 || response.status >= 300) {
                console.log("error while retrieving users")
            } else {
                setUsers(response.data);
            }
        } catch (err) {
            console.log("error while retrieving users: " + err)
        }

    }, [responseGetUsers]);


    const submitHandler = async (event) => {
        event.preventDefault();
        setUserEdited(false);
        const errorValues = {password: false, email: false, messages: []}

        if (!EMAIL_RE.test(formValues.email)) {
            errorValues.email = true;
            errorValues.messages.push("L'adresse mail n'est pas valide.");
        }
        setError(errorValues)

        if (errorValues.messages.length > 0) {
            return;
        }

        console.log({data: {
                id: formValues.id,
                firstname: formValues.firstName,
                lastname: formValues.lastName,
                email: formValues.email,
                phone: formValues.phone,
                roles: formValues.roles,
                company: formValues.company,
                status: true
            }})

        await sendPutRequest({
            id: 'putUser',
            url: '/api/users/putUser',
            data: {
                id: formValues.id,
                firstname: formValues.firstName,
                lastname: formValues.lastName,
                email: formValues.email,
                phone: formValues.phone,
                roles: formValues.roles,
                company: formValues.company,
                status: true
            },
            headers: {
                Authorization: `Bearer ${reduxUser.token}`,
            },
        })
    }

    useEffect(() => {
        if (!responsePut?.status) return;

        const errorValues = (message) => {
            return {
                email: false,
                password: false,
                messages: [message]
            }
        }

        try {
            if (responsePut?.status >= 200 && responsePut?.status < 300) {
                setUserEdited(true);
            } else if (responsePut?.status === 409) {
                setError(errorValues("Cette adresse email est déjà utilisée."));
            } else {
                setError(errorValues("Une erreur serveur est survenue."));
            }
        } catch (err) {
            setError(errorValues("Une erreur serveur est survenue."));
        }

    }, [responsePut])

    const handleChange = (e) => {
        const {name, value} = e.target;
        let newValue = value;

        if (name === 'phone') {
            newValue = value.replace(/\D/g, '').slice(0, 14);
        }
        if (name === 'roles') {
            setUtilValues({...utilValues, selectedRole: value});
            newValue = ROLE_HIERARCHY[value];
        }
        setFormValues((prev) => ({...prev, [name]: newValue}));
    }

    const handleUserSelect = (e) => {
        const selectedId = e.target.value;

        const selectedUser = users.find(u => u.id === selectedId);
        setSelectedUser(selectedUser);
        console.log(selectedUser);
        if (selectedUser && selectedCompany) {

            setFormValues({
                id: selectedUser.id,
                firstName: selectedUser.firstname,
                lastName: selectedUser.lastname,
                phone: selectedUser.phone,
                email: selectedUser.email,
                company: selectedUser.company.id,
                roles: selectedUser.roles,
            });

            setUtilValues({
                ...utilValues,
                selectedRole: getHighestRole(selectedUser.roles),
            });
        }
    }

    const handleSelectCompany = (e) => {
        const selectedId = e.target.value;
        const selectedCompany = companies.find(c => c.id === selectedId)
        if (selectedCompany){
            setSelectedUser(null);
            setSelectedCompany(selectedCompany);
        }
    }

    const getHighestRole = (roles) => {
        if (roles.includes("ROLE_MASTER")) return "ROLE_MASTER";
        if (roles.includes("ROLE_MANAGER")) return "ROLE_MANAGER";
        return "ROLE_USER";
    }

    return <Grid container direction='column'>
        <Grid item>
            <Header/>
        </Grid>
        <Grid item style={{marginTop: 20}}>
            <div className={classes.paper}>
                <Typography
                    component="h1"
                    variant="h3"
                    align="center"
                    gutterBottom
                >
                    Modifier un utilisateur
                </Typography>
                {error.messages.map((m, i) => (
                    <Typography id="error-info" key={i}
                                style={{display: "block"}}
                                className={classes.message} align="center">
                        {m}
                    </Typography>)
                )}

                {userEdited && (<Typography id="added-user"
                                           className={classes.confirm}
                                           align="center">
                        L'utilisateur a bien été modifié.
                    </Typography>

                )}

                <form className={classes.form} onSubmit={submitHandler} required>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined" required>
                                <InputLabel id="company-label">Entreprise</InputLabel>
                                <Select
                                    labelId="company-label"
                                    id="company"
                                    name="company"
                                    value={selectedCompany}
                                    onChange={handleSelectCompany}
                                    label="Entreprise"
                                >
                                    {companies
                                        .sort((a,b) => {
                                            if (a.name > b.name) return 1;
                                            if (b.name > a.name) return -1;
                                            return 0})
                                        .map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name} - {c.siret}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined" required>
                                <InputLabel id="userselect-label">Utilisateur</InputLabel>
                                <Select
                                    labelId="userselect-label"
                                    id="user"
                                    name="user"
                                    value={formValues.id}
                                    onChange={handleUserSelect}
                                    label="Utilisateur"
                                    disabled={!selectedCompany}
                                >
                                    {users
                                        .sort((a,b) => {
                                            if (a.lastname > b.lastname) return 1;
                                            if (b.lastname > a.lastname) return -1;
                                            return 0})
                                        .map((u) => (
                                        <MenuItem key={u.id} value={u.id}>
                                            {u.firstname} {u.lastname.toUpperCase()}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {selectedUser && (<>
                        <Grid item xs={12} sm={11}>
                            <Divider/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField variant="outlined" required
                                       fullWidth label="Prénom"
                                       name="firstName"
                                       value={formValues.firstName}
                                       onChange={handleChange}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField variant="outlined" required
                                       fullWidth label="Nom"
                                       name="lastName"
                                       value={formValues.lastName}
                                       onChange={handleChange}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField variant="outlined" required
                                       fullWidth label="Numéro de téléphone"
                                       name="phone"
                                       value={formValues.phone}
                                       onChange={handleChange}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField variant="outlined" required
                                       fullWidth label="Adresse email"
                                       name="email"
                                       value={formValues.email}
                                       error={error.email}
                                       onChange={handleChange}/>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined" required>
                                <InputLabel id="company-label">Entreprise</InputLabel>
                                <Select
                                    labelId="company-label"
                                    id="company"
                                    name="company"
                                    value={formValues.company}
                                    onChange={handleChange}
                                    label="Entreprise"
                                >
                                    {companies.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name} - {c.siret}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormLabel id="radio-roles">Rôles</FormLabel>
                            <RadioGroup
                                aria-labelledby="radio-roles"
                                defaultValue="ROLE_USER"
                                name="roles"
                                value={utilValues.selectedRole}
                                onChange={handleChange}
                                row
                            >
                                <FormControlLabel value="ROLE_MASTER"
                                                  control={<Radio/>}
                                                  label="Administrateur (ROLE_MASTER)"/>
                                <FormControlLabel value="ROLE_MANAGER"
                                                  control={<Radio/>}
                                                  label="Manager (ROLE_MANAGER)"/>
                                <FormControlLabel value="ROLE_USER"
                                                  control={<Radio/>}
                                                  label="Utilisateur (ROLE_USER)"/>
                            </RadioGroup>
                        </Grid>

                        <Grid item xs={12}>
                        <div style={{display: "flex", alignItems: "center", justifyContent: "center",}}>
                            <Button type="submit" variant="contained" className={classes.submit}>
                                ENVOYER
                            </Button>
                        </div>
                        </Grid>
                        </>
                        )}
                    </Grid>
                </form>
            </div>
        </Grid>
        <Box mt={5}>
            <Copyright/>
        </Box>
    </Grid>
}

export default EditUser;
